/**
 * Custom Variation Pricing
 * Handles special pricing and quantity display for products with SKU variations
 */

class VariationPricing {
  constructor(productInfo, sectionId = null) {
    this.productInfo = productInfo;
    this.sectionId = sectionId;
    this.init();
  }

  init() {
    // Subscribe to variant change events
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
    }

    // Listen for quantity changes
    const quantityInput = this.productInfo.querySelector('input[name="quantity"]');
    if (quantityInput) {
      quantityInput.addEventListener('change', this.handleQuantityChange.bind(this));
      quantityInput.addEventListener('input', this.handleQuantityChange.bind(this));
    }

    // Also listen for quantity button clicks
    const quantityButtons = this.productInfo.querySelectorAll('.quantity__button');
    quantityButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => this.handleQuantityChange(), 50);
      });
    });
  }

  /**
   * Get variant option name (the name of the option, not the value)
   */
  getVariantOptionName(product) {
    // Return the first option name (e.g., "Size", "Color", etc.)
    if (product.options && product.options.length > 0) {
      return product.options[0];
    }
    return '';
  }

  /**
   * Singularize or keep plural based on quantity
   * Assumes the word is already in plural form
   */
  adjustForQuantity(word, quantity) {
    // If quantity is 1, singularize; otherwise keep as is (already plural)
    if (quantity === 1) {
      return this.singularize(word);
    }
    return word;
  }

  /**
   * Singularize a plural word
   */
  singularize(word) {
    if (!word) return word;

    const lowerWord = word.toLowerCase();

    // Special cases
    if (lowerWord === 'sizes') return 'Size';
    if (lowerWord === 'colors' || lowerWord === 'colours') return 'Color';
    if (lowerWord === 'stones') return 'Stone';

    // Words ending in 'ies' -> 'y'
    if (word.endsWith('ies') && word.length > 3) {
      return word.slice(0, -3) + 'y';
    }

    // Words ending in 'es' (special cases like boxes, watches)
    if (word.endsWith('es') && word.length > 2) {
      const withoutEs = word.slice(0, -2);
      // Check if it's a word that uses 'es' plural (ends in s, x, z, ch, sh)
      if (withoutEs.endsWith('x') || withoutEs.endsWith('z') ||
          withoutEs.endsWith('ch') || withoutEs.endsWith('sh') ||
          withoutEs.endsWith('s')) {
        return withoutEs;
      }
    }

    // Words ending in 's' -> remove 's'
    if (word.endsWith('s') && word.length > 1) {
      return word.slice(0, -1);
    }

    // Default: return as is
    return word;
  }

  /**
   * Extract variation quantity from SKU (text after '-')
   */
  getVariationQuantity(sku) {
    if (!sku || !sku.includes('-')) {
      return null;
    }

    const skuParts = sku.split('-');
    const qty = parseInt(skuParts[1]);
    return (qty && qty > 0) ? qty : null;
  }

  /**
   * Format money using Shopify's format
   */
  formatMoney(cents, format) {
    if (!format) return '';

    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/g, value);
  }

  /**
   * Get current quantity from input
   */
  getCurrentQuantity() {
    const quantityInput = this.productInfo.querySelector('input[name="quantity"]');
    return quantityInput ? parseInt(quantityInput.value) || 1 : 1;
  }

  /**
   * Update prices based on variation quantity
   */
  updatePrices(variant, pageQuantity, moneyFormat, moneyWithCurrencyFormat, useCurrency, product) {
    if (!variant) return;

    const variationQuantity = this.getVariationQuantity(variant.sku);

    // Only apply custom pricing if SKU has a '-'
    if (variationQuantity === null) {
      return; // Don't modify prices if no variation quantity
    }

    const format = useCurrency ? moneyWithCurrencyFormat : moneyFormat;

    // Fixed: Only multiply variant price by selected quantity (not by variation quantity)
    // The variant price already includes the variation quantity pricing
    const adjustedPrice = variant.price * pageQuantity;
    const adjustedComparePrice = variant.compare_at_price ? variant.compare_at_price * pageQuantity : 0;

    // Update price displays
    const priceDisplays = this.productInfo.querySelectorAll('[data-price-display]');
    priceDisplays.forEach(display => {
      display.textContent = this.formatMoney(adjustedPrice, format);
    });

    // Update compare at price if exists
    const comparePriceDisplays = this.productInfo.querySelectorAll('.price-item--regular s, s.price-item--regular');
    if (adjustedComparePrice > adjustedPrice) {
      comparePriceDisplays.forEach(display => {
        display.textContent = this.formatMoney(adjustedComparePrice, format);
      });
    }

    // Update quantity display
    const quantityDisplay = this.productInfo.querySelector('[data-quantity-display]');
    if (quantityDisplay) {
      const totalQuantity = variationQuantity * pageQuantity;
      const variantOptionName = this.getVariantOptionName(product);
      const adjustedOptionName = this.adjustForQuantity(variantOptionName, totalQuantity);
      quantityDisplay.textContent = `${totalQuantity} Total ${adjustedOptionName}`;
    }

    // Update SKU display
    const skuDisplay = this.productInfo.querySelector('[data-sku-value]');
    if (skuDisplay && variant.sku) {
      const skuParts = variant.sku.split('-');
      skuDisplay.textContent = skuParts[0];
    }
  }

  /**
   * Handle variant change event
   */
  handleVariantChange(event) {
    const data = event.data || event;

    // Check if this event is for our section
    if (this.sectionId && data.sectionId !== this.sectionId) {
      return;
    }

    if (!data.variant) return;

    // Get product data and settings from the data attributes
    const productData = this.productInfo.dataset.productData;
    const moneyFormat = this.productInfo.dataset.moneyFormat;
    const moneyWithCurrencyFormat = this.productInfo.dataset.moneyWithCurrencyFormat;
    const useCurrency = this.productInfo.dataset.useCurrency === 'true';

    if (productData) {
      try {
        const product = JSON.parse(productData);
        this.updatePrices(
          data.variant,
          this.getCurrentQuantity(),
          moneyFormat,
          moneyWithCurrencyFormat,
          useCurrency,
          product
        );
      } catch (e) {
        console.error('Error parsing product data:', e);
      }
    }
  }

  /**
   * Handle quantity change event
   */
  handleQuantityChange() {
    // Get current variant
    const variantInput = this.productInfo.querySelector('input[name="id"]');
    if (!variantInput) return;

    const productData = this.productInfo.dataset.productData;
    const moneyFormat = this.productInfo.dataset.moneyFormat;
    const moneyWithCurrencyFormat = this.productInfo.dataset.moneyWithCurrencyFormat;
    const useCurrency = this.productInfo.dataset.useCurrency === 'true';

    if (productData) {
      try {
        const product = JSON.parse(productData);
        const variantId = parseInt(variantInput.value);
        const variant = product.variants.find(v => v.id === variantId);

        if (variant) {
          this.updatePrices(
            variant,
            this.getCurrentQuantity(),
            moneyFormat,
            moneyWithCurrencyFormat,
            useCurrency,
            product
          );
        }
      } catch (e) {
        console.error('Error parsing product data:', e);
      }
    }
  }
}

// Initialize variation pricing when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  // Initialize for main product pages
  const mainProductInfo = document.querySelector('product-info[id^="MainProduct"]');
  if (mainProductInfo) {
    new VariationPricing(mainProductInfo);
  }

  // Initialize for featured product sections
  const featuredProductInfos = document.querySelectorAll('product-info[data-section]');
  featuredProductInfos.forEach(productInfo => {
    const sectionId = productInfo.dataset.section;
    if (sectionId) {
      new VariationPricing(productInfo, sectionId);
    }
  });
});
